// EPSE Simulation Dashboard - All charts and save functionality
import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Activity, Battery, TrendingDown, Zap, AlertTriangle, Save, Loader2, 
  BarChart3, PieChart
} from 'lucide-react';
import { BESSConfig, SimulationKPIs, LiveChartPoint } from '@/types/epse';
import { EnergyBalanceSummary, EnergyBalancePoint, MonthlyBalance, calculateMonthlyBalance } from '@/lib/epseEnergyBalance';
import { EPSEDayProfileChart } from './EPSEDayProfileChart';
import { EPSEEnergyMixDonut } from './EPSEEnergyMixDonut';
import { EPSEMethodologyPanelEnhanced } from './EPSEMethodologyPanelEnhanced';
import { EPSEMonthlyBalance } from './EPSEMonthlyBalance';
import { EPSEDurationCurve } from './EPSEDurationCurve';
import { SyntheticProfileConfig } from '@/types/epse';
import { generateDailyProfile } from '@/lib/epseSyntheticProfile';
import {
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

interface EPSESimulationDashboardProps {
  // Chart data
  liveChartData: LiveChartPoint[];
  liveKpis: SimulationKPIs;
  isCalculating: boolean;
  
  // Energy balance (for synthetic)
  energyBalance: { points: EnergyBalancePoint[]; summary: EnergyBalanceSummary } | null;
  syntheticConfig: SyntheticProfileConfig;
  
  // Connection and BESS
  connectionLimit: number;
  bessConfig: BESSConfig;
  
  // Season
  selectedSeason: 'winter' | 'zomer';
  onSeasonChange: (season: 'winter' | 'zomer') => void;
  peakKw: number;
  
  // Save functionality
  onSave: (name: string) => Promise<void>;
  isSaving: boolean;
  
  // Quarter data for duration curve
  quarterData?: any[];
  profileMode: 'measured' | 'synthetic';
}

export function EPSESimulationDashboard({
  liveChartData,
  liveKpis,
  isCalculating,
  energyBalance,
  syntheticConfig,
  connectionLimit,
  bessConfig,
  selectedSeason,
  onSeasonChange,
  peakKw,
  onSave,
  isSaving,
  quarterData,
  profileMode,
}: EPSESimulationDashboardProps) {
  const [simulationName, setSimulationName] = useState('');

  const handleSave = async () => {
    const name = simulationName || `Simulatie ${new Date().toLocaleString('nl-NL')}`;
    await onSave(name);
    setSimulationName('');
  };

  // Format chart data - show hour every 4 quarters
  const formattedData = liveChartData.map((point, i) => ({
    ...point,
    displayTime: i % 4 === 0 ? point.time : '', // time is now just "0", "1", etc.
  }));

  // Calculate monthly balance for synthetic profiles
  const monthlyData = useMemo(() => {
    if (profileMode !== 'synthetic' || !syntheticConfig) return [];
    return calculateMonthlyBalance(syntheticConfig, bessConfig, (config, season, isWeekend) => 
      generateDailyProfile(config, season as 'winter' | 'lente' | 'zomer' | 'herfst', isWeekend)
    );
  }, [syntheticConfig, bessConfig, profileMode]);

  return (
    <div className="space-y-6">
      {/* Live KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={isCalculating ? 'opacity-50 transition-opacity' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingDown className="h-4 w-4" />
              Piekreductie
            </div>
            <p className="text-2xl font-bold mt-1">
              {liveKpis.peak_reduction_kw.toFixed(0)} <span className="text-sm font-normal">kW</span>
            </p>
            <p className="text-xs text-muted-foreground">
              {liveKpis.peak_reduction_percent.toFixed(1)}% reductie
            </p>
          </CardContent>
        </Card>

        <Card className={isCalculating ? 'opacity-50 transition-opacity' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Overschrijdingen
            </div>
            <p className="text-2xl font-bold mt-1">{liveKpis.peak_overruns_count}</p>
            <p className="text-xs text-muted-foreground">
              {liveKpis.peak_overruns_duration_hours.toFixed(1)} uur totaal
            </p>
          </CardContent>
        </Card>

        <Card className={isCalculating ? 'opacity-50 transition-opacity' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Battery className="h-4 w-4" />
              Batterijcycli
            </div>
            <p className="text-2xl font-bold mt-1">{liveKpis.bess_cycles.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">
              {liveKpis.bess_throughput_kwh.toFixed(0)} kWh doorvoer
            </p>
          </CardContent>
        </Card>

        <Card className={isCalculating ? 'opacity-50 transition-opacity' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Zap className="h-4 w-4" />
              Energie boven limiet
            </div>
            <p className="text-2xl font-bold mt-1">
              {liveKpis.energy_above_limit_kwh.toFixed(1)} <span className="text-sm font-normal">kWh</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Te vermijden met grotere BESS
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main BESS Simulation Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            BESS Simulatie
            {isCalculating && <Badge variant="secondary" className="animate-pulse">Berekenen...</Badge>}
          </CardTitle>
          <CardDescription>
            Energieprofiel met batterij actie ({selectedSeason === 'winter' ? 'Winter' : 'Zomer'} dagprofiel)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="displayTime" tick={{ fontSize: 10 }} interval={0} />
                <YAxis 
                  yAxisId="power"
                  tick={{ fontSize: 10 }}
                  label={{ value: 'kW', angle: -90, position: 'insideLeft', fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="soc"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  domain={[0, 100]}
                  label={{ value: 'SOC %', angle: 90, position: 'insideRight', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = {
                      load: 'Belasting',
                      netLoad: 'Netto belasting',
                      batteryAction: 'Batterij',
                      socPercent: 'SOC',
                      shortfall_kw: 'Tekort',
                    };
                    const unit = name === 'socPercent' ? '%' : ' kW';
                    return [`${value.toFixed(1)}${unit}`, labels[name] || name];
                  }}
                />
                <Legend />
                <ReferenceLine
                  y={connectionLimit}
                  yAxisId="power"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  label={{
                    value: `Limiet: ${connectionLimit} kW`,
                    position: 'right',
                    fill: 'hsl(var(--destructive))',
                    fontSize: 11,
                    fontWeight: 'bold',
                  }}
                />
                <Area
                  yAxisId="power"
                  type="monotone"
                  dataKey="load"
                  name="Belasting"
                  fill="hsl(var(--primary) / 0.3)"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="power"
                  type="monotone"
                  dataKey="netLoad"
                  name="Netto belasting"
                  stroke="hsl(var(--chart-2))"
                  strokeWidth={2}
                  dot={false}
                />
                <Bar
                  yAxisId="power"
                  dataKey="batteryAction"
                  name="Batterij"
                  fill="hsl(var(--chart-3))"
                  opacity={0.7}
                />
                {/* Shortfall bars (red) - remaining demand that can't be covered */}
                <Bar
                  yAxisId="power"
                  dataKey="shortfall_kw"
                  name="Tekort"
                  fill="hsl(var(--destructive))"
                  opacity={0.8}
                />
                <Line
                  yAxisId="soc"
                  type="monotone"
                  dataKey="socPercent"
                  name="SOC"
                  stroke="hsl(var(--chart-4))"
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Day Profile and Energy Mix (for synthetic) */}
      {profileMode === 'synthetic' && energyBalance && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EPSEDayProfileChart
            points={energyBalance.points}
            summary={energyBalance.summary}
            connectionLimit={syntheticConfig.connection_kw_limit}
            season={selectedSeason}
            onSeasonChange={onSeasonChange}
            peakKw={peakKw}
          />
          
          <div className="space-y-6">
            <EPSEEnergyMixDonut summary={energyBalance.summary} title="Dagelijkse Energiemix" />
            <EPSEMethodologyPanelEnhanced config={syntheticConfig} summary={energyBalance.summary} />
          </div>
        </div>
      )}

      {/* Duration Curve (for measured data) */}
      {profileMode === 'measured' && quarterData && quarterData.length > 0 && (
        <EPSEDurationCurve quarterData={quarterData} connectionLimit={connectionLimit} />
      )}

      {/* Monthly Energy Balance Chart (for synthetic) */}
      {profileMode === 'synthetic' && monthlyData.length > 0 && (
        <EPSEMonthlyBalance data={monthlyData} />
      )}
      {/* BESS Summary */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-4">
              <Badge variant="outline">
                <Battery className="h-3 w-3 mr-1" />
                {bessConfig.capacity_kwh} kWh / {bessConfig.power_kw} kW
              </Badge>
              <span className="text-muted-foreground">
                C-rate: {(bessConfig.power_kw / bessConfig.capacity_kwh).toFixed(2)}C
              </span>
            </div>
            <span className="text-muted-foreground">
              Efficiëntie: {(bessConfig.efficiency_charge * 100).toFixed(0)}% laden / {(bessConfig.efficiency_discharge * 100).toFixed(0)}% ontladen
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Save Simulation */}
      <Card className="border-primary/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            Simulatie Opslaan
          </CardTitle>
          <CardDescription>
            Bewaar deze configuratie en resultaten voor later vergelijken
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="sim-name">Simulatienaam (optioneel)</Label>
              <Input
                id="sim-name"
                value={simulationName}
                onChange={(e) => setSimulationName(e.target.value)}
                placeholder={`Simulatie ${new Date().toLocaleString('nl-NL')}`}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Opslaan
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
